import BigNumber from 'bignumber.js'

import { numberFormatter } from '../formatters/numberFormatter.js'
import { assert, NumberLike } from '../index.js'
import { bigNumberify } from '../math/bigNumber.js'
import { NormalizedNumber } from './NormalizedNumber.js'

type Value = BaseNumber | number

export interface BaseNumber {
  toNormalizedUnit(decimals: number): NormalizedNumber
  toBigNumber(): BigNumber
  toBigInt(): bigint
  toString(base?: number): string
  isZero(): boolean
  toFixed(decimalPlaces?: number): string
  toJSON(): string
  valueOf(): string

  times(n: BaseNumber, base?: number): BaseNumber
  div(n: BaseNumber, base?: number, roundingMode?: BigNumber.RoundingMode): BaseNumber
  minus(n: BaseNumber, base?: number): BaseNumber
  plus(n: BaseNumber, base?: number): BaseNumber
  mod(n: BaseNumber, base?: number): BaseNumber

  negated(): BaseNumber
  abs(): BaseNumber
  eq(n: Value, base?: number): boolean
  gt(n: Value, base?: number): boolean
  gte(n: Value, base?: number): boolean
  lt(n: Value, base?: number): boolean
  lte(n: Value, base?: number): boolean
  comparedTo(n: Value, base?: number): number
  isPositive(): boolean
  isNegative(): boolean
  isFinite(): boolean
  toNumber(): number
}

export class BaseNumberClass implements BaseNumber {
  private readonly value: BigNumber
  private readonly asString: string

  constructor(value: NumberLike) {
    const result = bigNumberify(value)
    this.asString = numberFormatter.withUnderscores(result)
    assert(!result.dp(), `Value ${this.asString} should not have decimal points in its representation.`)
    this.value = result
  }

  static isInstance(value: unknown): value is BaseNumber {
    return value instanceof BaseNumberClass
  }

  toNormalizedUnit(decimals: number): NormalizedNumber {
    return NormalizedNumber(this.value.shiftedBy(-decimals))
  }

  toBigNumber(): BigNumber {
    return this.value
  }

  toBigInt(): bigint {
    return BigInt(this.toFixed())
  }

  toString(base?: number): string {
    return this.value.toString(base)
  }

  isZero(): boolean {
    return this.value.isZero()
  }

  toFixed(decimalPlaces?: number): string {
    if (decimalPlaces === undefined) {
      return this.value.toFixed()
    }
    return this.value.toFixed(decimalPlaces)
  }

  toJSON(): string {
    return this.value.toJSON()
  }

  valueOf(): string {
    return this.value.valueOf()
  }

  times(n: BaseNumber, base?: number): BaseNumber {
    return BaseNumber(this.value.times(n.toBigNumber(), base))
  }

  div(n: BaseNumber, base?: number, roundingMode?: BigNumber.RoundingMode): BaseNumber {
    return BaseNumber(this.value.div(n.toBigNumber(), base).integerValue(roundingMode))
  }

  minus(n: BaseNumber, base?: number): BaseNumber {
    return BaseNumber(this.value.minus(n.toBigNumber(), base))
  }

  plus(n: BaseNumber, base?: number): BaseNumber {
    return BaseNumber(this.value.plus(n.toBigNumber(), base))
  }

  mod(n: BaseNumber, base?: number): BaseNumber {
    return BaseNumber(this.value.mod(n.toBigNumber(), base))
  }

  negated(): BaseNumber {
    return BaseNumber(this.value.negated())
  }

  abs(): BaseNumber {
    return BaseNumber(this.value.abs())
  }

  toNumber(): number {
    return this.value.toNumber()
  }

  eq(n: Value, base?: number): boolean {
    return this.value.eq(getValue(n), base)
  }

  gt(n: Value, base?: number): boolean {
    return this.value.gt(getValue(n), base)
  }

  gte(n: Value, base?: number): boolean {
    return this.value.gte(getValue(n), base)
  }

  lt(n: Value, base?: number): boolean {
    return this.value.lt(getValue(n), base)
  }

  lte(n: Value, base?: number): boolean {
    return this.value.lte(getValue(n), base)
  }

  comparedTo(n: Value, base?: number): number {
    return this.value.comparedTo(getValue(n), base)
  }

  isPositive(): boolean {
    return this.value.isPositive()
  }

  isNegative(): boolean {
    return this.value.isNegative()
  }

  isFinite(): boolean {
    return this.value.isFinite()
  }
}

function getValue(value: Value): number | BigNumber {
  if (typeof value === 'number') {
    return value
  }
  return value.toBigNumber()
}

function BaseNumberFunction(value: NumberLike): BaseNumberClass {
  return new BaseNumberClass(value)
}
interface StaticBaseNumber {
  new (value: NumberLike): BaseNumber
  (value: NumberLike): BaseNumber
  readonly prototype: BaseNumber
  isInstance(value: unknown): value is BaseNumber
}

Object.setPrototypeOf(BaseNumberFunction, BaseNumberClass)
BaseNumberFunction.prototype = BaseNumberClass.prototype

/**
 * Represents a base number i.e. 1.5 * 10^18 (DAI)
 */
export const BaseNumber: StaticBaseNumber = Object.assign(BaseNumberFunction, BaseNumberClass)
